import {
    PropertiesPanelContent,
    PropertiesPanelPropertyConfig
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import { T } from 'src/shared/helpers';
import React from 'react';
import { Icon } from 'src/components/widgets/icon';
import PointLightIcon from 'src/shared/material_icons/action/lightbulb.svg';
import SpotLightIcon from 'src/shared/material_icons/editor/highlight.svg';
import DirectionalLightIcon from 'src/shared/material_icons/image/wb_sunny.svg';
import HemisphericLightIcon from 'src/shared/material_icons/image/wb_twilight.svg';

export type LightConfigHelpers = {
    updateProperty: <K extends keyof LightNode>(key: K, value: LightNode[K]) => void;
    updateVector: (vectorType: 'position' | 'direction', axis: 'x' | 'y' | 'z', value: number) => void;
    handleColorChange: (key: 'diffuseColor' | 'groundColor') => (_: any, color: string) => void;
    /** Riporta la luce al centro e a guardare in giù. */
    resetTransform: () => void;
};

// SOTTO IL NOME CI VA LA PAROLA, non il nome del dato. «point_light» è come il motore 3D chiama
// quel tipo di luce; chi guarda legge «Point light». Sta qui e non nel pannello perché la
// leggono in due — la testata, che la scrive sotto il nome, e la card del tipo.
export const LIGHT_KIND: Record<string, string> = {
    point_light: T._('Point light', 'Admin'),
    directional_light: T._('Directional light', 'Admin'),
    spot_light: T._('Spot light', 'Admin'),
    hemispheric_light: T._('Hemispheric light', 'Admin')
};

// OGNI SPECIE HA LA SUA ICONA (prototipo D166, `lightIcon(type)`): nell'Outliner, nella testata, nel menu del
// tipo, nell'Add e nella selezione multipla. Icone Material, come quelle di Look (utente, 2026-10-14).
const LIGHT_GLYPH: Record<string, React.FC> = {
    point_light: PointLightIcon,
    spot_light: SpotLightIcon,
    directional_light: DirectionalLightIcon,
    hemispheric_light: HemisphericLightIcon
};

export const lightIcon = (lightType: string) => {
    const Glyph = LIGHT_GLYPH[lightType] ?? PointLightIcon;
    return (
        <Icon>
            <Glyph />
        </Icon>
    );
};

// Le quattro specie come le chiede un menu, con la loro icona. Derivate dalla tavola qui sopra e non
// riscritte: due elenchi degli stessi quattro nomi sono uno che un giorno resta indietro.
export const LIGHT_KIND_OPTIONS = Object.entries(LIGHT_KIND).map(([value, label]) => ({ value, label, mark: lightIcon(value) }));

// LA RIGA CHE SPIEGA OGNI SPECIE, nel menu di Add (prototipo D166): in parole di chi illumina una scena.
export const LIGHT_KIND_HINT: Record<string, string> = {
    point_light: T._('Shines in every direction', 'Admin'),
    spot_light: T._('A cone pointed at something', 'Admin'),
    directional_light: T._('Parallel rays, like the sun', 'Admin'),
    hemispheric_light: T._('Soft light from the whole sky', 'Admin')
};

// LE STESSE QUATTRO SPECIE IN UNA PAROLA, per il codino di una riga d'elenco. Qui la tavola
// sopra non basta e non si può ricavarla togliendo «light» dalla sua: quella parola in un'altra
// lingua non sta in fondo, e in certe non c'è affatto. Sono quattro etichette, e come tali si
// traducono.
export const LIGHT_KIND_SHORT: Record<string, string> = {
    point_light: T._('Point', 'Admin'),
    directional_light: T._('Directional', 'Admin'),
    spot_light: T._('Spot', 'Admin'),
    hemispheric_light: T._('Hemispheric', 'Admin')
};

export type LightNode = {
    id: string;
    name: string;
    lightType: string;
    /** Accesa o spenta nella scena. È lo stesso dato che l'occhio della lista commuta. */
    active?: boolean;
    intensity: number;
    diffuseColor: string;
    groundColor?: string;
    position?: [number, number, number];
    direction?: [number, number, number];
    angle?: number;
    /** Quanto sfuma il bordo del cono di un faretto: 0 netto, 100 dal centro. */
    falloff?: number;
    /** Fin dove arriva il cursore dell'intensità: per puntiformi e faretti dipende dalla scena. */
    intensityMax?: number;
};

// IL VERSO A RIPOSO DI UNA LUCE, lo stesso di «Reset transform»: in giù, una luce che illumina dal
// cielo. Per una emisferica la direzione è dove sta l'ALTO, e in giù la ribalterebbe. Sta qui perché
// lo chiedono in tre: chi la fa nascere, chi ne cambia la specie e chi la riporta a riposo.
export const restingDirection = (lightType: string): [number, number, number] => (lightType === 'hemispheric_light' ? [0, 1, 0] : [0, -1, 0]);

const createPropertyConfig = <T extends PropertiesPanelContent>(config: T) => config;

// Utility per normalizzare colori hex
const normalizeHexColor = (color: string): string => {
    if (!color) return '#FFFFFF';

    // Se non inizia con #, aggiungilo
    if (!color.startsWith('#')) {
        color = '#' + color;
    }

    // Se non è valido, ritorna bianco
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
        console.warn('Invalid hex color:', color, 'using white as fallback');
        return '#FFFFFF';
    }

    // Assicurati che sia maiuscolo
    return color.toUpperCase();
};

// Configurazione per un input triplo (x, y, z) per vettori
const createVectorConfig = (
    id: string,
    label: string,
    values: [number, number, number],
    onChange: (axis: 'x' | 'y' | 'z', value: number) => void
): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'point',
        id,
        label,
        coords: [
            {
                id: 'x',
                label: 'X',
                step: 0.01,
                value: values[0],
                onChange: (newValue) => onChange('x', newValue)
            },
            {
                id: 'y',
                label: 'Y',
                step: 0.01,
                value: values[1],
                onChange: (newValue) => onChange('y', newValue)
            },
            {
                id: 'z',
                label: 'Z',
                step: 0.01,
                value: values[2],
                onChange: (newValue) => onChange('z', newValue)
            }
        ]
    });
};

// LA COLONNA HA DUE CAPITOLI, e le card dentro portano i nomi del mockup.
//
// Prima erano due gruppi piatti — «Basic Properties» e «Light Type Properties» — e il secondo
// nome non dice niente a chi guarda: raccoglieva la posizione di una luce puntiforme insieme al
// colore del terreno di una emisferica, cioè cose di natura diversa messe insieme dall'unica
// proprietà che avevano in comune, essere condizionali.
//
// Ora il criterio è quello del mockup: DOVE sta la luce (Transform) e COM'È fatta la sua
// luce (Light). Le proprietà condizionali cadono nel capitolo che le riguarda, e una
// card vuota non si mostra: una luce emisferica non ha posizione, e un contenitore che si apre
// sul niente dice che manca qualcosa.

const transformProperties = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelPropertyConfig[] => {
    const properties: PropertiesPanelPropertyConfig[] = [];

    // La posizione ce l'hanno la puntiforme e il faretto: una direzionale illumina da un verso, e
    // una emisferica arriva da tutto il cielo.
    if (light.lightType === 'point_light' || light.lightType === 'spot_light') {
        properties.push(
            createVectorConfig('position', T._('Position', 'Admin'), light.position || [0, 0, 0], (axis, value) =>
                helpers.updateVector('position', axis, value)
            )
        );
    }

    // ANCHE UN CIELO HA UN VERSO, e mancava. Per una emisferica la direzione è dove sta l'ALTO: da
    // lì scende il colore del cielo e dalla parte opposta sale quello del suolo, e inclinarla
    // ribalta il gradiente. Non ha una posizione — un cielo non sta da nessuna parte — ma dire dov'è
    // il suo alto è la metà del suo mestiere, ed era l'unica specie senza card Transform.
    const aims = light.lightType === 'directional_light' || light.lightType === 'spot_light' || light.lightType === 'hemispheric_light';

    if (aims) {
        const fallback: [number, number, number] = light.lightType === 'hemispheric_light' ? [0, 1, 0] : [0, -1, 0];

        properties.push(
            createVectorConfig('direction', T._('Direction', 'Admin'), light.direction || fallback, (axis, value) =>
                helpers.updateVector('direction', axis, value)
            )
        );
    }

    // AZZERARE STA SOTTO I NUMERI e non sopra, come nel pannello di un oggetto: la si sceglie
    // dopo aver visto che cosa si sta per buttare via. Per una luce «azzerata» vuol dire al
    // centro della scena e rivolta in giù — non c'è una scala né una rotazione da riportare a
    // uno. E non si scrive se non c'è niente da azzerare: una emisferica è un cielo, non sta da
    // nessuna parte.
    if (properties.length) {
        properties.push(
            createPropertyConfig({
                type: 'action',
                id: 'light-reset-transform',
                label: T._('Reset transform', 'Admin'),
                onClick: helpers.resetTransform
            })
        );
    }

    return properties;
};

const lightProperties = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelPropertyConfig[] => [
    // ACCESA È LO STESSO DATO DELL'OCCHIO NELLA LISTA, e deve restare uno. Sta in testa a Light (utente, 2026-10-20):
    // del Settings del prototipo — Active, Cast shadow, Visible, Locked — il motore regge solo questa, e una tab
    // per una riga sola è un clic in più per niente.
    createPropertyConfig({
        type: 'switch',
        id: 'light-active',
        label: T._('Active', 'Admin'),
        checked: light.active !== false,
        onChange: (checked: boolean) => helpers.updateProperty('active', checked)
    }),
    // IL NOME NON STA QUI: sta nella testata dello Inspector, dove si legge e si modifica. Due
    // campi per la stessa cosa, uno sopra lo altro, sembrano due nomi.
    createPropertyConfig({
        type: 'range',
        id: 'intensity',
        label: T._('Intensity', 'Admin'),
        value: typeof light.intensity === 'number' ? light.intensity : 1,
        // FINO A ZERO (utente, 2026-10-15): una luce a zero è una luce spenta senza toglierla, ed è il
        // gesto di chi prova l'illuminazione — si guarda la scena senza di lei e poi la si riaccende.
        min: 0,
        max: light.intensityMax ?? 10,
        step: 0.1,
        onChange: (value) => helpers.updateProperty('intensity', value)
    }),
    createPropertyConfig({
        type: 'color',
        id: 'diffuseColor',
        label: T._('Color', 'Admin'),
        value: normalizeHexColor(light.diffuseColor),
        onChange: (value) => helpers.handleColorChange('diffuseColor')(null, value)
    })
];

// LA SECTION DEL TIPO (prototipo D130): ciò che cambia cambiando specie, col nome della specie.
// Un faretto ha il suo cono, un'emisferica il suolo; puntiforme e direzionale non hanno niente di loro qui —
// il Range del prototipo i materiali PBR non lo leggono —, e una section vuota sarebbe una promessa.
const lightTypeGroup = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelPropertyConfig | null => {
    if (light.lightType === 'spot_light') {
        return createPropertyConfig({
            type: 'group',
            id: 'light-spot',
            header: T._('Spot', 'Admin'),
            properties: [
                createPropertyConfig({
                    type: 'range',
                    id: 'angle',
                    label: T._('Cone angle', 'Admin'),
                    value: typeof light.angle === 'number' ? light.angle : 30,
                    min: 0,
                    max: 180,
                    step: 0.1,
                    unit: '°',
                    onChange: (value) => helpers.updateProperty('angle', value)
                }),
                // IL BORDO DEL CONO, quanto sfuma (QA, 2026-10). Babylon lo fa con `innerAngle` e la caduta glTF:
                // 0 è un bordo netto, 100 sfuma dal centro. `exponent`, che si scriveva prima, i PBR non lo leggono.
                createPropertyConfig({
                    type: 'range',
                    id: 'falloff',
                    label: T._('Edge falloff', 'Admin'),
                    value: typeof light.falloff === 'number' ? light.falloff : 100,
                    min: 0,
                    max: 100,
                    step: 1,
                    unit: '%',
                    onChange: (value) => helpers.updateProperty('falloff', value)
                })
            ]
        });
    }

    // Il colore del terreno è la seconda metà di una emisferica: sopra il cielo, sotto il suolo.
    if (light.lightType === 'hemispheric_light') {
        return createPropertyConfig({
            type: 'group',
            id: 'light-hemispheric',
            header: T._('Hemispheric', 'Admin'),
            properties: [
                createPropertyConfig({
                    type: 'color',
                    id: 'groundColor',
                    label: T._('Ground color', 'Admin'),
                    value: normalizeHexColor(light.groundColor || '#000000'),
                    onChange: (value) => helpers.handleColorChange('groundColor')(null, value)
                })
            ]
        });
    }

    return null;
};

// IL PANNELLO DI UNA LUCE NON HA TAB (utente, 2026-10-20): quello che la luce È e dove sta — la trasformazione,
// la luce che emette, i parametri del suo tipo. Un capitolo solo non mostra né tab né etichetta.
const lightPropertiesByType = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelContent[] => {
    const sections: PropertiesPanelContent[] = [];
    const transform = transformProperties(light, helpers);

    // IL TIPO NON È PIÙ UNA CARD: si sceglie dalla pastiglia della testata (`InspectorShell`,
    // `kindChoice`), che lo scrive già. Cambiarlo rifà la luce — vedi `changeLightType` in `lightsEditor.tsx`.
    const scene: PropertiesPanelPropertyConfig[] = [];

    if (transform.length) {
        scene.push(
            createPropertyConfig({
                type: 'group',
                id: 'light-transform',
                header: T._('Transform', 'Admin'),
                flat: true,
                defaultIsOpen: true,
                properties: transform
            })
        );
    }

    scene.push(
        createPropertyConfig({
            type: 'group',
            id: 'light-light',
            header: T._('Light', 'Admin'),
            properties: lightProperties(light, helpers)
        })
    );

    const typeGroup = lightTypeGroup(light, helpers);
    if (typeGroup) scene.push(typeGroup);

    sections.push(
        createPropertyConfig({
            type: 'section',
            id: 'light-general',
            title: T._('General', 'Admin'),
            properties: scene
        })
    );

    return sections;
};

// Hook principale da esportare (simile a useObjectPropertiesCreator)
export const useLightPropertiesCreator = (): ((light: LightNode, helpers: LightConfigHelpers) => PropertiesPanelContent[]) => {
    return (light: LightNode, helpers: LightConfigHelpers) => {
        return lightPropertiesByType(light, helpers);
    };
};
